import * as Sentry from '@sentry/browser';
import { matchPath } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import jwtDecode from 'jwt-decode';

import { authorizedRouteMap } from './authorizedRouteMap';
import Log from 'utilities/log';
import history from 'browserHistory';

const HOST_URL = process.env.REACT_APP_HOST_URL || 'http://localhost:3004';
const AZURE_CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID || 'b2b83b4f-f688-466b-af46-04e84f9b939c';
const AZURE_TENANT_ID = process.env.REACT_APP_AZURE_TENANT_ID || 'fd600fd5-ea40-49ca-8c01-1c8bf0727dd2';
const AZURE_REDIRECT_URI = process.env.REACT_APP_AZURE_REDIRECT_URI || `${HOST_URL}/callback`;
const DEFAULT_AZURE_SCOPES = ['openid', 'profile', 'email', `${AZURE_CLIENT_ID}/.default`];

const AZURE_SCOPES = (process.env.REACT_APP_AZURE_SCOPES || '')
  .split(/[,\s]+/g)
  .map(s => s.trim())
  .filter(Boolean);

const loginRequest = {
  scopes: AZURE_SCOPES.length > 0 ? AZURE_SCOPES : DEFAULT_AZURE_SCOPES
};
const msalConfig = {
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${AZURE_TENANT_ID}`,
    redirectUri: AZURE_REDIRECT_URI,
    postLogoutRedirectUri: HOST_URL,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false
  },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0:
            console.error('[MSAL Error]', message);
            return;
          case 1:
            console.warn('[MSAL Warning]', message);
            return;
          case 2:
            console.info('[MSAL Info]', message);
            return;
          case 3:
            console.debug('[MSAL Debug]', message);
            return;
          default:
            return;
        }
      }
    }
  }
};

class Auth {
  accessToken;
  idToken;
  expiresAt;
  userRoles;
  userPermissions;
  userMetadata;
  msalInstance;
  isInitialized = false;

  constructor() {
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.getIdToken = this.getIdToken.bind(this);
    this.renewSession = this.renewSession.bind(this);
    this.userHasRole = this.userHasRole.bind(this);
    this.userHasPermission = this.userHasPermission.bind(this);
  }

  async ensureInitialized() {
    if (!this.isInitialized) {
      if (!this.msalInstance) {
        this.msalInstance = new PublicClientApplication(msalConfig);
      }
      await this.msalInstance.initialize();
      this.isInitialized = true;
      console.log('[MSAL] Initialized successfully');
    }
  }

  async handleAuthentication() {
    try {
      await this.ensureInitialized();
      console.log('[Auth] Handling authentication...');

      const response = await this.msalInstance.handleRedirectPromise();

      if (response) {
        console.log('[Auth] Authentication response received:', response);
        this.setSession(response, '/authorizations');
      } else {
        console.log('[Auth] No redirect response');
        const accounts = this.msalInstance.getAllAccounts();
        console.log('[Auth] Existing accounts:', accounts.length);

        if (accounts.length > 0) {
          this.msalInstance.setActiveAccount(accounts[0]);
          console.log('[Auth] Active account set:', accounts[0].username);

          try {
            const silentResponse = await this.msalInstance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0]
            });
            this.setSession(silentResponse);
          } catch (silentError) {
            console.warn('[Auth] Silent token acquisition failed:', silentError);
          }
        }
      }
    } catch (err) {
      console.error('[Auth] Handle authentication error:', err);
      Log.error(`handle authentication error: ${err.toString()}`);
      this.clearInteractionState();

      history.replace('/');
    }
  }

  clearInteractionState() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('msal.') && (key.includes('.interaction.') || key.includes('interaction.status'))) {
          console.log('[Auth] Clearing stale interaction key:', key);
          localStorage.removeItem(key);
        }
      });
    } catch (err) {
      console.error('[Auth] Error clearing interaction state:', err);
    }
  }

  getAccessToken() {
    return this.accessToken;
  }

  getIdToken() {
    return this.idToken;
  }

  parseToken = token => {
    try {
      const tokenData = jwtDecode(token);
      console.log('[Auth] Parsed token:', tokenData);
      return tokenData ? tokenData : null;
    } catch (err) {
      console.error('[Auth] Token parse error:', err);
      Log.error(`auth token parse error: ${err.toString()}`);
      return null;
    }
  };

  getUserPermissionsList = () => {
    return this.userPermissions || [];
  };

  getUserRoleList = () => {
    return this.userRoles || [];
  };

  getUserMetadata = () => {
    return this.userMetadata || {};
  };

  userHasPermission = permission => {
    const userPermissions = this.getUserPermissionsList();
    if (userPermissions.some(up => up === 'admin:all')) {
      return true;
    }
    const hasUserPermission = userPermissions.some(up => up === permission);
    return hasUserPermission;
  };

  isPathAuthorized = path => {
    const permissions = this.getUserPermissionsList();
    const requiredPermissionKey = Object.keys(authorizedRouteMap).find(key =>
      matchPath(path, {
        path: key,
        exact: true,
        strict: false
      })
    );
    if (requiredPermissionKey) {
      return permissions.findIndex(p => p === authorizedRouteMap[requiredPermissionKey]) > -1;
    }
    return true;
  };

  getRolesFromToken = parsedToken => {
    const raw = parsedToken?.roles ?? parsedToken?.role ?? [];
    const roles = Array.isArray(raw) ? raw : [raw];

    return roles.filter(Boolean).map(r => String(r).trim());
  };

  getEffectiveRoles = parsedToken => {
    const roles = this.getRolesFromToken(parsedToken);

    const isAdmin = roles.some(r => {
      const normalized = r.toLowerCase();
      return normalized === 'task.admin' || normalized === 'task.admins' || normalized === 'admin';
    });

    return isAdmin ? [...new Set([...roles, 'Task.Admin'])] : roles;
  };

  userHasRole = role => {
    const roles = this.getUserRoleList();
    //console.log(roles);
    return roles.findIndex(r => r === role) > -1;
  };

  setSession(authResult, redirectTo) {
    console.log('[Auth] Setting session with result:', authResult);

    if (this.isSettingSession) {
      console.log('[Auth] Already setting session, skipping');
      return;
    }
    this.isSettingSession = true;

    localStorage.setItem('isLoggedIn', 'true');

    this.expiresAt = authResult.expiresOn ? authResult.expiresOn.getTime() : new Date().getTime() + 3600000;
    this.accessToken = authResult.accessToken;
    this.idToken = authResult.idToken;

    if (authResult.account) {
      this.msalInstance.setActiveAccount(authResult.account);
    }

    const parsedToken = this.parseToken(this.accessToken);
    const roles = this.getEffectiveRoles(parsedToken);
    this.userRoles = roles;
    this.userPermissions = this.mapRolesToPermissions(roles);
    console.log('[Auth] User roles from token:', roles);

    this.userMetadata = {
      name: parsedToken['name'],
      email: parsedToken['preferred_username'] || parsedToken['email'],
      oid: parsedToken['oid']
    };

    console.log('[Auth] Session set:', {
      roles: this.userRoles,
      permissions: this.userPermissions,
      metadata: this.userMetadata
    });

    let path = localStorage.getItem('redirectAfterLogin') || '/';

    if (path === '/login' || path === '/callback') {
      path = '/';
    }

    localStorage.removeItem('redirectAfterLogin');
    console.log('[Auth] Redirecting to:', path);

    if (redirectTo) {
      setTimeout(() => {
        this.isSettingSession = false;
        history.replace(redirectTo);
      }, 100);
    } else {
      this.isSettingSession = false;
    }
  }
  mapRolesToPermissions(roles) {
    const permissionMap = {
      'Task.read': ['selfService:read:auths'],
      'Task.write': ['selfService:write:benefits', 'submit'],
      'Task.Reports': ['reports:read'],
      'Task.Admin': ['admin:all', 'submit'],
      'Task.ReadWrite': ['selfService:read:auths', 'selfService:write:benefits', 'submit'],
      'File.Intake.FullAccess': ['intake:fullAccess']
    };

    const permissions = [];
    roles.forEach(role => {
      if (permissionMap[role]) {
        permissions.push(...permissionMap[role]);
      }
    });

    return [...new Set(permissions)];
  }

  async renewSession() {
    try {
      await this.ensureInitialized();
      console.log('[Auth] Renewing session...');
      const accounts = this.msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        console.log('[Auth] No accounts found for renewal');
        this.clearSession();
        return false;
      }

      const account = accounts[0];
      this.msalInstance.setActiveAccount(account);

      const silentRequest = {
        ...loginRequest,
        account: account
      };

      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      console.log('[Auth] Token renewed successfully');
      this.setSession(response);
      return true;
    } catch (err) {
      console.error('[Auth] Token renewal error:', err);

      if (err.name === 'InteractionRequiredAuthError') {
        console.log('[Auth] Interaction required for renewal');
        this.clearSession();
        return false;
      }

      Log.error(`Could not get a new token: ${err ? err.toString() : 'Unknown'}`);
      return false;
    }
  }

  async login() {
    try {
      await this.ensureInitialized();
      console.log('[Auth] Starting login...');
      this.clearInteractionState();
      await new Promise(resolve => setTimeout(resolve, 100));

      await this.msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[Auth] Login error:', err);
      Log.error(`Login error: ${err.toString()}`);

      if (err.errorCode === 'interaction_in_progress') {
        console.log('[Auth] Clearing interaction state and will retry...');
        this.clearInteractionState();
      }
    }
  }

  clearSession() {
    console.log('[Auth] Clearing session (local only)');

    this.accessToken = null;
    this.idToken = null;
    this.expiresAt = 0;
    this.userRoles = [];
    this.userPermissions = [];
    this.userMetadata = {};

    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('access_token');

    Sentry.setUser(null);
  }

  async logout() {
    console.log('[Auth] Logging out...');

    await this.ensureInitialized();
    const account = this.msalInstance.getActiveAccount();

    this.clearSession();

    if (account) {
      const logoutRequest = {
        account: account,
        postLogoutRedirectUri: HOST_URL
      };

      try {
        await this.msalInstance.logoutRedirect(logoutRequest);
      } catch (err) {
        console.error('[Auth] Logout error:', err);
        Log.error(`Logout error: ${err.toString()}`);
        window.location.href = HOST_URL;
      }
    } else {
      console.log('[Auth] No active account, redirecting to home');
      window.location.href = HOST_URL;
    }
  }

  isAuthenticated() {
    if (!this.msalInstance) return false;
    const accounts = this.msalInstance.getAllAccounts();
    const tokenValid = this.expiresAt && new Date().getTime() < this.expiresAt;
    return accounts.length > 0 && tokenValid;
  }

  requestPasswordReset(email) {
    return new Promise(resolve => {
      window.location.href = 'https://account.activedirectory.windowsazure.com/ChangePassword.aspx';
      resolve();
    });
  }
}

export default new Auth();
